import React, { useState } from 'react'
import { LuCheck, LuTriangleAlert } from 'react-icons/lu'

import Select from '@/components/select/Select'
import { Button } from '@/components/ui/button'
import {
  BulkUpdatePayload,
  ReferralsImportItem,
  ReferralsImportItemPartial,
} from '@/redux/slices/referralsImportBase'

interface Props {
  importItem: ReferralsImportItem
  updateImportItem: (item: ReferralsImportItemPartial) => void
  bulkUpdateItems: (payload: BulkUpdatePayload) => void
  bulkUpdateSelectedOnly: boolean
}

interface AdviserOption {
  label: string
  value: number
}

const ToCompanyAdviserCell: React.FC<Props> = ({
  importItem,
  updateImportItem,
  bulkUpdateItems,
  bulkUpdateSelectedOnly,
}) => {
  const [isChanging, setIsChanging] = useState(false)
  const toCompanyUsers = importItem.toCompany?.users || []

  const adviserOptions: AdviserOption[] = toCompanyUsers.map((user) => ({
    label: `${user.firstName} ${user.lastName}`,
    value: user.id,
  }))
  if (importItem.toAdviserEmail) {
    adviserOptions.unshift({
      label: importItem.toAdviserEmail,
      value: -1,
    })
  }

  function handleChange(selectedOption: AdviserOption | null) {
    const toCompanyUser = selectedOption
      ? toCompanyUsers.find((user) => user.id === selectedOption.value) || null
      : null

    updateImportItem({
      refUUID: importItem.refUUID,
      toCompanyUser: toCompanyUser,
      toCompanyUserDefaultUsed: false,
    })

    setIsChanging(false)
  }

  function handleBulkApply() {
    bulkUpdateItems({
      filter: {
        selected: bulkUpdateSelectedOnly,
        toCompany: importItem.toCompany?.id,
      },
      data: {
        toCompanyUser: importItem.toCompanyUser,
        toCompanyUserDefaultUsed: false,
      },
    })
  }

  const bulkUpdateScope =
    importItem.bulkToCompanyCount > 1
      ? !bulkUpdateSelectedOnly
        ? 'all'
        : importItem.selected
          ? 'selected'
          : 'none'
      : 'none'

  if (!importItem.toCompany) {
    return null
  }

  if (isChanging) {
    return (
      <>
        <Select
          options={adviserOptions}
          onChange={handleChange}
          onBlur={() => setIsChanging(false)}
          value={adviserOptions.find(
            (adviserOption) =>
              adviserOption.value == importItem.toCompanyUser?.id,
          )}
          className="mt-1"
        />

        <span className="mt-1 block text-right">
          <Button
            size="icon-xs"
            variant="outline-primary"
            onClick={() => setIsChanging(false)}
          >
            <LuCheck />
          </Button>
        </span>
      </>
    )
  }

  if (importItem.toCompanyUser) {
    return (
      <span className="text-dim text-xs">
        <span
          onClick={() => setIsChanging(true)}
          className="cursor-pointer font-semibold"
        >
          {importItem.toCompanyUser.firstName}{' '}
          {importItem.toCompanyUser.lastName}
        </span>
        , <span className="break-all">{importItem.toCompanyUser.email}</span>
        {importItem.toCompanyUserDefaultUsed && (
          <>
            <span className="text-warning"> (default)</span>
          </>
        )}
        {importItem.toCompanyUserDefaultUsed &&
          importItem.sourceToAdviserEmail != '' && (
            <>
              ,<br />
              <span
                className="text-warning break-all"
                style={{ maxWidth: '300px' }}
              >
                <LuTriangleAlert className="mr-1 inline-block" />
                adviser with email address “{importItem.sourceToAdviserEmail}”
                not found, select from available advisers or leave default
              </span>
            </>
          )}
        <span className="show-on-row-hover">
          ,{' '}
          <Button variant="plain" onClick={() => setIsChanging(true)}>
            change
          </Button>
          {bulkUpdateScope == 'all' && (
            <>
              {' or '}
              <Button
                variant="plain"
                onClick={handleBulkApply}
                title="Apply this adviser for all referrals for this company"
              >
                apply&nbsp;to&nbsp;all&nbsp;for&nbsp;this&nbsp;company&nbsp;(
                {importItem.bulkToCompanyCount})
              </Button>
            </>
          )}
          {bulkUpdateScope == 'selected' && (
            <>
              {' or '}
              <Button
                variant="plain"
                onClick={handleBulkApply}
                title="Apply this adviser for selected referrals for this company"
              >
                apply&nbsp;to&nbsp;selected&nbsp;for&nbsp;this&nbsp;company
              </Button>
            </>
          )}
        </span>
      </span>
    )
  }

  if (adviserOptions.length > 0) {
    return (
      <span className="text-dim text-xs">
        {importItem.isToInvite
          ? 'New adviser selected'
          : 'No specific adviser selected'}
        <span className="show-on-row-hover">
          ,{' '}
          <Button variant="plain" onClick={() => setIsChanging(true)}>
            choose
          </Button>
        </span>
      </span>
    )
  }

  return (
    <>
      <div className="text-dim mt-1 text-xs">
        <LuTriangleAlert className="mr-1 inline-block" />
        Not possible to select adviser. No advisers available for this company.
      </div>
    </>
  )
}

export default ToCompanyAdviserCell
